import { api } from '@/lib/api/requests'

// Partner-invite (fixed-pair doubles/mixed) cabinet + picker reads and the four
// mutations. Backend: BE-1 (invite/cancel/accept/decline) + BE-2 (candidates +
// /tournaments/me). Actor = the authenticated registered participant; the club
// is resolved server-side from the tournament. All paths use the canonical
// tournamentId (NOT shortId — shortId is only for the public deep-link).

export type PartnerStatusKind = 'solo' | 'invite_sent' | 'invite_received' | 'paired'

export type PartnerInviter = { inviterPlayerId: string; inviterName: string }

// The viewer's partner state for ONE registration (only present on fixed-pair
// doubles/mixed; null for singles / americano / mexicano / KotC).
export type PartnerState =
  | { status: 'solo' }
  | { status: 'invite_sent'; inviteePlayerId: string; inviteeName: string }
  | { status: 'invite_received'; inviters: PartnerInviter[] }
  | { status: 'paired'; partnerName: string }

export type MyTournamentRow = {
  tournamentId: string
  shortId: string
  name: string
  orgId?: string
  orgName?: string | null
  sport?: string
  system?: string
  eventType?: string | null
  startDate?: string | null
  status?: string
  partner?: PartnerState | null
  /**
   * Present only on a CHILD's entry (the viewer is his guardian); the adult row has none.
   * `waiting_waiver`: entered and held, out of the draw until the guardian accepts the club's
   * tournament waiver. Never key a row by `tournamentId` alone — see `rowKey`.
   */
  child?: { dependentId: string; displayName: string | null; entryState: 'entered' | 'waiting_waiver' }
  /**
   * WHAT THE VIEWER OWES ON THIS ENTRY, carried down WITH the list: the cabinet is the
   * one screen most likely to hold ten rows at once, and a row that asked for its own
   * fee would be the N+1 this repo forbids on a list. The seat's own frozen number, not
   * the tournament's current fee — an organizer who raises the price has not changed
   * what anybody already owes. `null` on a tournament that predates the seat ledger, and
   * on a withdrawn entry: history, not a debt.
   *
   * NO `paidVia`. The player's screens never name a method, and a field that is not sent
   * cannot be rendered by mistake.
   *
   * `packageUse` / `packageOffer` (§5.12 item 1, backend 597fb041): the `scope=self` seat
   * read's own two shapes, so this row's fee block draws the tournament page's package lines.
   * Read by `myEntrySeatFromRow`, never here.
   */
  entrySeat?: {
    payableMinor?: number | null
    currency?: string | null
    paymentStatus?: string | null
    packageUse?: unknown
    packageOffer?: unknown
  } | null
}

export type MyTournamentsResponse = { tournaments?: MyTournamentRow[] }

export type PartnerCandidate = {
  playerId: string
  displayName: string
  level: number | null
  eligible: boolean
  reason: string | null
  invitedYou: boolean
}

export type PartnerCandidatesResponse = {
  candidates?: PartnerCandidate[]
  // The actor's current outgoing invite (the invitee's id), if any.
  mySentInviteeId?: string | null
  // Set when the actor is already paired — the picker shows nothing actionable.
  alreadyPaired?: boolean
}

// GET /tournaments/me — the viewer's current (non-finished) registrations, each
// with partner status. Soonest first (server-sorted).
export function getMyTournaments(): Promise<MyTournamentsResponse> {
  return api.get<MyTournamentsResponse>('/tournaments/me', { handleAuthErrors: false })
}

// GET /tournaments/:id/partner/candidates — unpaired co-registrants of THIS
// tournament only (roster-only; never the club roster), with eligibility.
export function getPartnerCandidates(tournamentId: string): Promise<PartnerCandidatesResponse> {
  return api.get<PartnerCandidatesResponse>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/candidates`,
    { handleAuthErrors: false },
  )
}

export function invitePartner(tournamentId: string, inviteePlayerId: string): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/invite`,
    { body: { inviteePlayerId }, handleAuthErrors: false },
  )
}

export function cancelPartnerInvite(tournamentId: string): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/cancel`,
    { handleAuthErrors: false },
  )
}

export function acceptPartnerInvite(
  tournamentId: string,
  inviterPlayerId: string,
): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/accept`,
    { body: { inviterPlayerId }, handleAuthErrors: false },
  )
}

export function declinePartnerInvite(
  tournamentId: string,
  inviterPlayerId: string,
): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/decline`,
    { body: { inviterPlayerId }, handleAuthErrors: false },
  )
}

// Un-pair (BE-3): the actor splits their "A + B" pair into two solo lines; both
// stay registered. 400 if not paired; 409 once the draw is generated (matches
// exist) or the tournament is finished/archived — the message is surfaced.
export function unpairPartner(tournamentId: string): Promise<Record<string, unknown>> {
  return api.post<Record<string, unknown>>(
    `/tournaments/${encodeURIComponent(tournamentId)}/partner/unpair`,
    { handleAuthErrors: false },
  )
}
